import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  Check,
  Copy,
  Download,
  ListChecks,
  Loader2,
  Lock,
  LockOpen,
  Rss,
  UserPlus,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addMeetingMember,
  clearMeetingSchedule,
  fetchMeeting,
  meetingIcsUrl,
  removeMeetingMember,
  setMeetingPrivacy,
  setMeetingSchedule,
  subscribeMeetingCalendar,
  unsubscribeMeetingCalendar,
  type Meeting,
  type MeetingMember,
  type Occurrence,
  type Recurrence,
} from "@/lib/api";
import { statusMeta } from "@/lib/topic-status";
import { cn } from "@/lib/utils";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** A small accessible on/off switch (mirrors MembersDialog's Toggle). */
function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50",
        checked ? "bg-brand-600" : "border border-border bg-elevated",
      )}
    >
      <span
        className={cn(
          "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

/**
 * Meeting detail: roster (owner-gated add/remove, mirrors MembersDialog), the standing agenda
 * (read-only for v1), a privacy toggle gated on `can_privatize`, and past occurrences — each
 * navigates to that occurrence's room. Opened from a meeting card's "Manage" gear.
 */
export function MeetingDetailDialog({
  open,
  onOpenChange,
  meetingId,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  meetingId: string | number;
  /** Called after any change (privacy flip, add/remove) so a caller can refresh its own list. */
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [members, setMembers] = useState<MeetingMember[]>([]);
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [myRole, setMyRole] = useState<MeetingMember["role"] | null>(null);
  const [canPrivatize, setCanPrivatize] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Schedule (Phase C) — owner-only. Seeded from the loaded meeting on every reload (see
  // `seedSchedule`); the form itself always writes a full Recurrence back on save.
  const [freq, setFreq] = useState<Recurrence["freq"]>("weekly");
  const [interval, setIntervalValue] = useState(1);
  const [byweekday, setByweekday] = useState<number[]>([1]);
  const [bymonthday, setBymonthday] = useState(1);
  const [time, setTime] = useState("09:00");
  const [tz, setTz] = useState(BROWSER_TZ);
  const [autoCreate, setAutoCreate] = useState(false);
  const [nextRunAt, setNextRunAt] = useState<string | null>(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Add-to-calendar (Phase D) — my OWN subscription, not owner-gated. `feedUrl` is a secret
  // capability link: it only ever lives in this component's state for the current dialog
  // session (never fetched on open, never logged/persisted) and is cleared whenever the
  // dialog is reopened or the meeting changes.
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [calBusy, setCalBusy] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);
  const [calNotice, setCalNotice] = useState<string | null>(null);

  const isOwner = myRole === "owner";

  function seedSchedule(m: Meeting) {
    const rec = m.recurrence ?? null;
    setFreq(rec?.freq ?? "weekly");
    setIntervalValue(rec?.interval ?? 1);
    setByweekday(rec?.byweekday && rec.byweekday.length ? rec.byweekday : [1]);
    setBymonthday(rec?.bymonthday ?? 1);
    setTime(rec?.time ?? "09:00");
    setTz(rec?.tz || BROWSER_TZ);
    setAutoCreate(!!m.auto_create);
    setNextRunAt(m.next_run_at ?? null);
    setScheduleError(null);
  }

  async function reload(signal?: AbortSignal) {
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchMeeting(meetingId, signal);
      setMeeting(detail.meeting);
      setMembers(detail.members);
      setOccurrences(detail.occurrences);
      setMyRole(detail.my_role);
      setCanPrivatize(!!detail.can_privatize);
      seedSchedule(detail.meeting);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load meeting");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setError(null);
    setFeedUrl(null);
    setCopied(false);
    setCalBusy(false);
    setCalError(null);
    setCalNotice(null);
    const ac = new AbortController();
    void reload(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meetingId]);

  async function togglePrivacy(next: boolean) {
    setBusy(true);
    setError(null);
    try {
      await setMeetingPrivacy(meetingId, next);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update privacy");
    } finally {
      setBusy(false);
    }
  }

  async function onAddMember(e: FormEvent) {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr) return;
    if (!addr.endsWith("@codetogether.com")) {
      setError("Email must be a @codetogether.com address");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await addMeetingMember(meetingId, addr);
      setEmail("");
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add member");
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveMember(addr: string) {
    setBusy(true);
    setError(null);
    try {
      await removeMeetingMember(meetingId, addr);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove member");
    } finally {
      setBusy(false);
    }
  }

  function goToOccurrence(occ: Occurrence) {
    onOpenChange(false);
    navigate(`/room/${occ.slug}`);
  }

  function toggleWeekday(day: number) {
    setByweekday((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
  }

  async function onSaveSchedule(e: FormEvent) {
    e.preventDefault();
    setScheduleBusy(true);
    setScheduleError(null);
    const recurrence: Recurrence =
      freq === "weekly"
        ? { freq, interval, byweekday, time, tz }
        : { freq, interval, bymonthday, time, tz };
    try {
      await setMeetingSchedule(meetingId, { recurrence, auto_create: autoCreate });
      await reload();
      onChanged?.();
    } catch (e) {
      setScheduleError(e instanceof Error ? e.message : "Failed to save schedule");
    } finally {
      setScheduleBusy(false);
    }
  }

  async function onTurnOffSchedule() {
    setScheduleBusy(true);
    setScheduleError(null);
    try {
      await clearMeetingSchedule(meetingId);
      await reload();
      onChanged?.();
    } catch (e) {
      setScheduleError(e instanceof Error ? e.message : "Failed to turn off schedule");
    } finally {
      setScheduleBusy(false);
    }
  }

  async function onSubscribeCalendar() {
    setCalBusy(true);
    setCalError(null);
    setCalNotice(null);
    try {
      const { feed_url } = await subscribeMeetingCalendar(meetingId);
      setFeedUrl(feed_url);
      setCopied(false);
    } catch (e) {
      setCalError(e instanceof Error ? e.message : "Failed to subscribe");
    } finally {
      setCalBusy(false);
    }
  }

  async function onUnsubscribeCalendar() {
    setCalBusy(true);
    setCalError(null);
    try {
      await unsubscribeMeetingCalendar(meetingId);
      setFeedUrl(null);
      setCopied(false);
      setCalNotice("Unsubscribed — that link no longer works.");
    } catch (e) {
      setCalError(e instanceof Error ? e.message : "Failed to unsubscribe");
    } finally {
      setCalBusy(false);
    }
  }

  async function onCopyFeedUrl() {
    if (!feedUrl) return;
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCalError("Couldn't copy automatically — select and copy the link by hand.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{meeting ? meeting.title : "Meeting"}</DialogTitle>
          <DialogDescription>
            Roster, standing agenda, privacy, and past occurrences for this series.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="size-5 animate-spin text-muted" />
          </div>
        ) : !meeting ? (
          <p className="py-4 text-sm text-red-500">{error ?? "Meeting not found"}</p>
        ) : (
          <div className="max-h-[70vh] space-y-5 overflow-y-auto">
            <div className="flex items-center justify-between gap-4 border-b border-border pb-4">
              <div className="flex min-w-0 items-center gap-2">
                {meeting.is_private ? (
                  <Lock className="size-4 shrink-0 text-muted" />
                ) : (
                  <LockOpen className="size-4 shrink-0 text-muted" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {meeting.is_private ? "Private" : "Public"}
                  </p>
                  <p className="mt-0.5 text-xs text-muted">
                    {meeting.is_private
                      ? "Only members below can see or join any occurrence."
                      : "Anyone in the org can see and join every occurrence."}
                  </p>
                </div>
              </div>
              {canPrivatize ? (
                <Toggle
                  checked={meeting.is_private}
                  disabled={busy}
                  onChange={togglePrivacy}
                  label={meeting.is_private ? "Make public" : "Make private"}
                />
              ) : null}
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Members</p>
              {members.length === 0 ? (
                <p className="py-1 text-sm text-muted">No members yet.</p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto">
                  {members.map((m) => (
                    <li
                      key={m.email}
                      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm"
                    >
                      <span className="min-w-0 truncate">{m.email}</span>
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                            m.role === "owner"
                              ? "bg-brand-600/10 text-brand-600"
                              : "bg-elevated text-muted",
                          )}
                        >
                          {m.role}
                        </span>
                        {isOwner && m.role !== "owner" ? (
                          <button
                            type="button"
                            aria-label={`Remove ${m.email}`}
                            disabled={busy}
                            onClick={() => void onRemoveMember(m.email)}
                            className="text-muted transition-colors hover:text-red-500 disabled:opacity-50"
                          >
                            <X className="size-3.5" />
                          </button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {isOwner ? (
                <form onSubmit={onAddMember} className="flex items-center gap-2 pt-1">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@codetogether.com"
                    disabled={busy}
                    className="min-w-0 flex-1 rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                  />
                  <Button type="submit" size="sm" disabled={busy || !email.trim()}>
                    {busy ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <UserPlus className="size-3.5" />
                    )}
                    Add
                  </Button>
                </form>
              ) : null}
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <ListChecks className="size-3.5 text-muted" />
                Standing agenda
              </p>
              {meeting.agenda.length === 0 ? (
                <p className="text-sm text-muted">
                  No standing agenda — occurrences still carry forward unfinished action items.
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {meeting.agenda.map((label, i) => (
                    <li key={`${label}-${i}`} className="rounded-md bg-surface px-3 py-1.5">
                      {label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <CalendarPlus className="size-3.5 text-muted" />
                Add to calendar
              </p>
              <p className="text-xs text-muted">
                This is your own subscription — it doesn't change anything for other members.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <a href={meetingIcsUrl(meeting.id)} download={`${meeting.slug}.ics`}>
                    <Download className="size-3.5" />
                    Download .ics
                  </a>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={calBusy}
                  onClick={() => void onSubscribeCalendar()}
                >
                  {calBusy ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Rss className="size-3.5" />
                  )}
                  {feedUrl ? "Re-subscribe" : "Subscribe in your calendar"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={calBusy}
                  onClick={() => void onUnsubscribeCalendar()}
                >
                  Unsubscribe
                </Button>
              </div>

              {feedUrl ? (
                <div className="space-y-1.5 rounded-md border border-border bg-surface p-3">
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={feedUrl}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label="Calendar feed URL"
                      className="min-w-0 flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-600"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void onCopyFeedUrl()}
                    >
                      {copied ? (
                        <Check className="size-3.5" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted">
                    In Google Calendar → Other calendars → + → From URL, paste this.
                  </p>
                </div>
              ) : null}

              {calNotice ? <p className="text-xs text-muted">{calNotice}</p> : null}
              {calError ? <p className="text-xs text-red-500">{calError}</p> : null}
              <p className="text-xs text-muted">
                The link stops working once you unsubscribe, or if you leave this meeting.
              </p>
            </div>

            {isOwner ? (
              <form
                onSubmit={onSaveSchedule}
                className="space-y-3 border-t border-border pt-4"
              >
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <CalendarClock className="size-3.5 text-muted" />
                  Schedule
                </p>

                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={freq}
                    disabled={scheduleBusy}
                    onChange={(e) => setFreq(e.target.value as Recurrence["freq"])}
                    className="rounded-md border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <span className="text-sm text-muted">every</span>
                  <input
                    type="number"
                    min={1}
                    value={interval}
                    disabled={scheduleBusy}
                    onChange={(e) => setIntervalValue(Math.max(1, Number(e.target.value) || 1))}
                    className="w-16 rounded-md border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                  />
                  <span className="text-sm text-muted">{freq === "weekly" ? "week(s)" : "month(s)"}</span>
                </div>

                {freq === "weekly" ? (
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAY_LABELS.map((label, day) => (
                      <button
                        key={label}
                        type="button"
                        disabled={scheduleBusy}
                        onClick={() => toggleWeekday(day)}
                        aria-pressed={byweekday.includes(day)}
                        className={cn(
                          "rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50",
                          byweekday.includes(day)
                            ? "border-brand-600 bg-brand-600/10 text-brand-600"
                            : "border-border bg-surface text-muted hover:text-text",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted">Day of month</span>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={bymonthday}
                      disabled={scheduleBusy}
                      onChange={(e) =>
                        setBymonthday(Math.min(31, Math.max(1, Number(e.target.value) || 1)))
                      }
                      className="w-16 rounded-md border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                    />
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-sm text-muted" htmlFor="schedule-time">
                    Time
                  </label>
                  <input
                    id="schedule-time"
                    type="time"
                    value={time}
                    disabled={scheduleBusy}
                    onChange={(e) => setTime(e.target.value)}
                    className="rounded-md border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                  />
                  <label className="text-sm text-muted" htmlFor="schedule-tz">
                    Timezone
                  </label>
                  <input
                    id="schedule-tz"
                    type="text"
                    value={tz}
                    disabled={scheduleBusy}
                    onChange={(e) => setTz(e.target.value)}
                    placeholder={BROWSER_TZ}
                    className="min-w-0 flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50"
                  />
                </div>

                <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">Auto-create occurrences</p>
                    <p className="text-xs text-muted">
                      When on, the server starts each occurrence for you when it's due.
                    </p>
                  </div>
                  <Toggle
                    checked={autoCreate}
                    disabled={scheduleBusy}
                    onChange={setAutoCreate}
                    label="Auto-create occurrences"
                  />
                </div>

                {nextRunAt ? (
                  <p className="text-xs text-muted">
                    Next run: {new Date(nextRunAt).toLocaleString()}
                  </p>
                ) : null}
                {scheduleError ? <p className="text-xs text-red-500">{scheduleError}</p> : null}

                <div className="flex items-center gap-2">
                  <Button type="submit" size="sm" disabled={scheduleBusy}>
                    {scheduleBusy ? <Loader2 className="size-3.5 animate-spin" /> : null}
                    Save schedule
                  </Button>
                  {meeting.recurrence ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={scheduleBusy}
                      onClick={() => void onTurnOffSchedule()}
                    >
                      Turn off schedule
                    </Button>
                  ) : null}
                </div>
              </form>
            ) : null}

            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-sm font-medium">Past occurrences</p>
              {occurrences.length === 0 ? (
                <p className="text-sm text-muted">
                  No occurrences yet — start this week's to kick off the series.
                </p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto">
                  {[...occurrences].reverse().map((occ) => {
                    const meta = statusMeta(occ.status);
                    return (
                      <li key={occ.id}>
                        <button
                          type="button"
                          onClick={() => goToOccurrence(occ)}
                          className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-left text-sm transition-colors hover:border-brand-600/50"
                        >
                          <span className="min-w-0 truncate">
                            Week {occ.occurrence_seq} · {occ.title}
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-xs text-muted">
                            {meta.label}
                            <ArrowRight className="size-3.5" />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {error ? <p className="text-xs text-red-500">{error}</p> : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
